import MenuIcon from "@mui/icons-material/Menu";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { styled, Typography, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { Box } from "@mui/system";
import { PuiIconButton, PuiLoadingBackdrop, useAlert } from "@portnet/ui";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { logoutAction } from "../../../modules/auth/login/state/loginActions";
import { LOGOUT_SUCCESSED_MESSAGE } from "../../router/others/routerMessages";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { Snackbar, Alert } from "@mui/material";
import instance from "../general/AxiosInterceptor";



const StyledPuiIconButton = styled((props) => <PuiIconButton {...props} />)(
    ({ theme }) => {
        return {
            transition: "all ease 400ms",
            color: "white",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
                backgroundColor: "white",
                color: theme.palette.primary.main,
            },
        };
    }
);

const AppToolbar = ({ height, onMenuIconClick, onMenuIconHover }) => {
    const user = useSelector((state) => state.auth.login.user);
    const isLogged = Boolean(user);
    const [isLogoutLoading, setIsLogoutLoading] = React.useState(false);
    const [openResetDialog, setOpenResetDialog] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState(null);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [severity, setSeverity] = React.useState("success");
    const [openWelcomeDialog, setOpenWelcomeDialog] = React.useState(false);

    const dispatch = useDispatch();
    const alertDispatch = useAlert();

    const WELCOME_SHOWN_KEY = "welcomeShown";

    React.useEffect(() => {
        if (isLogged && user?.description && !sessionStorage.getItem(WELCOME_SHOWN_KEY)) {
            setOpenWelcomeDialog(true);
            sessionStorage.setItem(WELCOME_SHOWN_KEY, "true");
        }
    }, [isLogged, user]);

    const logoutHandler = () => {
        setIsLogoutLoading(true);
        setTimeout(() => {
            alertDispatch("success", LOGOUT_SUCCESSED_MESSAGE);
            dispatch(logoutAction());
        }, 1000);
    };

    const handleConfirmReset = async () => {
        try {
            const response = await instance.delete("/resetMFA");
            setAlertMessage(response?.data?.message || "MFA réinitialisé avec succès");
            setSeverity("success");
            setAlertOpen(true);
            setOpenResetDialog(false);
        } catch (error) {
            setAlertMessage(error?.response?.data?.message || "Une erreur est survenue");
            setSeverity("error");
            setAlertOpen(true);
            setOpenResetDialog(false);
        }
    };

    const handleSnackClose = (event, reason) => {
        if (reason === "clickaway") return;
        setAlertOpen(false);
    };

    return (
        <>
            <PuiLoadingBackdrop open={isLogoutLoading} />
            <AppBar color="primary" sx={{ zIndex: 3 }}>
                <Toolbar sx={{ padding: "8px 19px !important", height }}>
                    {isLogged && (
                        <StyledPuiIconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={{ mr: 2 }}
                            onClick={onMenuIconClick}
                            onMouseEnter={onMenuIconHover}
                            onMouseLeave={onMenuIconHover}
                        >
                            <MenuIcon />
                        </StyledPuiIconButton>
                    )}

                    <Box sx={{ flexGrow: 1 }}>
                        <img
                            src="./portnet-logo-white.png"
                            srcSet="/portnet-logo-white.png"
                            alt="Logo portnet"
                            style={{ width: "150px" }}
                        />
                    </Box>

                    {isLogged && user?.description && (
                        <Tooltip
                            title={`Société : ${user.description}${user?.code ? ` — Code douane : ${user.code}` : ""}`}
                            arrow
                            placement="bottom"
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    mr: { xs: 1, sm: 2 },
                                    pl: 0.75,
                                    pr: 2,
                                    py: 0.75,
                                    maxWidth: { xs: 180, sm: 280, md: 380 },
                                    borderRadius: 999,
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                    border: "1px solid rgba(255,255,255,0.28)",
                                    backdropFilter: "blur(6px)",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                    transition: "all ease 250ms",
                                    cursor: "default",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.22)",
                                        boxShadow: "0 3px 12px rgba(0,0,0,0.2)",
                                    },
                                }}
                            >
                                <BusinessIcon sx={{ color: "white", fontSize: 22, flexShrink: 0 }} />
                                <Box sx={{ overflow: "hidden", minWidth: 0, lineHeight: 1.2 }}>
                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.7)",
                                            fontSize: 10,
                                            fontWeight: 600,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {user?.code ? `Code douane ${user.code}` : "Société"}
                                    </Typography>
                                    <Typography
                                        noWrap
                                        sx={{
                                            color: "white",
                                            fontSize: 14.5,
                                            fontWeight: 700,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {user.description}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    )}

                 {isLogged && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <StyledPuiIconButton
                            title="Réinitialiser MFA"
                            sx={{ mr: 1 }} 
                            onClick={() => setOpenResetDialog(true)}
                        >
                            <SettingsBackupRestoreIcon />
                        </StyledPuiIconButton>
                        <StyledPuiIconButton
                            title="Se déconnecter"
                            onClick={logoutHandler}
                        >
                            <PowerSettingsNewIcon />
                        </StyledPuiIconButton>
                    </div>
                )}
                </Toolbar>
            </AppBar>

            <Dialog
                open={openWelcomeDialog}
                onClose={() => setOpenWelcomeDialog(false)}
                PaperProps={{ sx: { borderRadius: 3, maxWidth: 340, width: "100%", overflow: "hidden" } }}
            >
                {/* Bandeau d'en-tête coloré */}
                <Box
                    sx={{
                        height: 64,
                        backgroundColor: "primary.main",
                        backgroundImage: (t) =>
                            `linear-gradient(135deg, ${t.palette.primary.light} 0%, ${t.palette.primary.dark} 100%)`,
                    }}
                />

                {/* Avatar société (chevauche l'en-tête) + badge de validation */}
                <Box sx={{ position: "relative", width: 64, height: 64, mx: "auto", mt: "-36px" }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            p: "4px",
                            borderRadius: "50%",
                            backgroundColor: "background.paper",
                            boxSizing: "border-box",
                            boxShadow: (t) => `0 6px 16px ${alpha(t.palette.primary.main, 0.3)}`,
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                backgroundImage: (t) =>
                                    `linear-gradient(135deg, ${t.palette.primary.light} 0%, ${t.palette.primary.dark} 100%)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography sx={{ color: "white", fontSize: 26, fontWeight: 800, lineHeight: 1 }}>
                                {user?.description?.trim()?.charAt(0)?.toUpperCase() || (
                                    <BusinessIcon sx={{ fontSize: 26 }} />
                                )}
                            </Typography>
                        </Box>
                    </Box>
                    <CheckCircleRoundedIcon
                        sx={{
                            position: "absolute",
                            right: 0,
                            bottom: 0,
                            fontSize: 22,
                            color: "success.main",
                            backgroundColor: "white",
                            borderRadius: "50%",
                        }}
                    />
                </Box>

                <Box sx={{ px: 2.75, pt: 1, pb: 2.25, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 17, color: "text.primary" }}>
                        Bienvenue
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 12.5, mt: 0.25 }}>
                        Vous êtes connecté en tant que :
                    </Typography>

                    {/* Bloc société */}
                    <Box
                        sx={{
                            mt: 1.75,
                            py: 1.5,
                            px: 1.5,
                            borderRadius: 2,
                            backgroundColor: (t) => alpha(t.palette.primary.main, 0.05),
                        }}
                    >
                        <Typography
                            sx={{ fontWeight: 700, fontSize: 15, color: "primary.main", lineHeight: 1.25 }}
                        >
                            {user?.description}
                        </Typography>
                        {user?.code && (
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    mt: 1.1,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 999,
                                    backgroundColor: "primary.main",
                                }}
                            >
                                <BusinessIcon sx={{ fontSize: 14, color: "white" }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "white" }}>
                                    Code douane : {user.code}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                <DialogActions sx={{ px: 2.75, pb: 2.25, pt: 0 }}>
                    <Button
                        onClick={() => setOpenWelcomeDialog(false)}
                        variant="contained"
                        color="primary"
                        fullWidth
                        disableElevation
                        sx={{
                            borderRadius: 1.5,
                            fontWeight: 600,
                            textTransform: "none",
                            py: 1,
                            fontSize: 14,
                            boxShadow: (t) => `0 5px 14px ${alpha(t.palette.primary.main, 0.3)}`,
                        }}
                    >
                        Continuer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
                <DialogTitle sx={{ textAlign: "center", fontWeight: "bolder" }}>
                    Réinitialisation MFA
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "primary.main" }}>
                        Voulez-vous vraiment réinitialiser votre MFA ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ margin: 2 }}>
                    <Button onClick={() => setOpenResetDialog(false)} color="secondary">
                        Annuler
                    </Button>
                    <Button onClick={handleConfirmReset} variant="contained" color="primary">
                        Oui
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alertOpen}
                autoHideDuration={3000}
                onClose={handleSnackClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleSnackClose} severity={severity} variant="filled" sx={{ width: "100%" }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AppToolbar;